import { useState } from 'react';
import { Settings, Mail, Check, Save, AlertCircle, Loader2, CalendarCheck, Home, Cpu, Shield, Gift, Server } from 'lucide-react';
import type { SystemConfig, ModelConfigData } from './types';
import { formatBeijingDateTime } from '../../utils/time';

interface ConfigForm {
  siteName: string;
  siteUrl: string;
  signupCredits: number;
  directorEnabled: boolean;
  canvasEnabled: boolean;
  ecommerceReplicateEnabled: boolean;
  pptEnabled: boolean;
  videoEnabled: boolean;
  photoRestoreEnabled: boolean;
  inviteEnabled: boolean;
  inviterCredits: number;
  inviteeCredits: number;
  invitePhoneOnly: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPass: string;
  smtpFrom: string;
  smtpFromName: string;
  emailProvider: 'smtp' | 'resend';
  resendApiKey: string;
  resendFromEmail: string;
  resendFromName: string;
  verifyCodeTemplate: string;
  errorAlertEnabled: boolean;
  errorAlertWindowMinutes: number;
  errorAlertThreshold: number;
  errorAlertRecipient: string;
  checkinEnabled: boolean;
  checkinMinCredits: number;
  checkinMaxCredits: number;
  heroTitle: string;
  heroSubtitle: string;
  heroFeature1: string;
  heroFeature2: string;
  heroFeature3: string;
  referenceImageMaxCount: number;
  defaultModelId: string;
  emailRegisterEnabled: boolean;
  phoneRegisterEnabled: boolean;
  allowedEmailDomains: string;
  ipRegisterDailyLimit: number;
  emailCodeIpHourlyLimit: number;
  emailCodeIpDailyLimit: number;
  captchaEnabled: boolean;
  emailCodePerEmailHourlyLimit: number;
  emailCodePerEmailDailyLimit: number;
  announcementAutoCollapseDelaySec: number;
  analyticsTrackingCode: string;
}

interface SettingsTabProps {
  config: SystemConfig | null;
  configForm: ConfigForm;
  onConfigChange: (form: ConfigForm) => void;
  onSave: () => void;
  onTestSMTP: () => void;
  onSendTestEmail: () => void;
  configSaved: boolean;
  smtpTesting: boolean;
  sendingTestEmail: boolean;
  testEmail: string;
  onTestEmailChange: (email: string) => void;
  models: ModelConfigData[];
}

type TabKey = 'basic' | 'image' | 'rewards' | 'security' | 'email';

const TABS: Array<{ key: TabKey; label: string; icon: typeof Settings }> = [
  { key: 'basic', label: '基础配置', icon: Settings },
  { key: 'image', label: '图片配置', icon: Cpu },
  { key: 'rewards', label: '奖励机制', icon: Gift },
  { key: 'security', label: '注册风控', icon: Shield },
  { key: 'email', label: '邮件配置', icon: Mail },
];

export default function SettingsTab(props: SettingsTabProps) {
  const { config, configForm, onConfigChange, onSave, configSaved } = props;
  const [activeTab, setActiveTab] = useState<TabKey>('basic');

  const setField = <K extends keyof ConfigForm>(field: K, value: ConfigForm[K]) => {
    onConfigChange({ ...configForm, [field]: value });
  };

  return (
    <div className="max-w-6xl">
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* 顶部标题 */}
        <div className="px-6 py-4 border-b border-light-border flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-blue rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-light-text">系统配置</h3>
            <p className="text-sm text-light-muted">管理系统所有配置项</p>
          </div>
        </div>

        {/* Tab 导航 */}
        <div className="px-6 border-b border-light-border flex items-center gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'border-accent-blue text-accent-blue'
                    : 'border-transparent text-light-muted hover:text-light-text'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab 内容 */}
        <div className="p-6">
          {activeTab === 'basic' && <BasicTab setField={setField} {...props} />}
          {activeTab === 'image' && <ImageTab setField={setField} {...props} />}
          {activeTab === 'rewards' && <RewardsTab setField={setField} {...props} />}
          {activeTab === 'security' && <SecurityTab setField={setField} {...props} />}
          {activeTab === 'email' && <EmailTab setField={setField} {...props} />}
        </div>

        {/* 保存按钮 + 状态 */}
        <div className="px-6 py-4 border-t border-light-border flex items-center gap-3 bg-light-hover/30">
          <button
            onClick={onSave}
            className="bg-accent-blue text-white font-medium px-6 py-2.5 rounded-xl hover:shadow-lg active:scale-[0.98] transition-all flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            保存配置
          </button>
          {configSaved && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <Check className="w-4 h-4" />
              已保存
            </span>
          )}
          {config?.updatedAt && (
            <span className="ml-auto text-xs text-light-muted">
              最后更新：{formatBeijingDateTime(config.updatedAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- 基础配置 ----------------

function BasicTab({
  configForm,
  setField,
}: SettingsTabProps & { setField: <K extends keyof ConfigForm>(f: K, v: ConfigForm[K]) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* 左栏：网站基础信息 + 注册积分 */}
      <div className="space-y-5">
        <SectionTitle icon={Settings} title="网站信息" color="bg-accent-blue" />

        <Field label="网站名称" hint="显示在页面标题和导航栏中">
          <Input
            value={configForm.siteName}
            onChange={(v) => setField('siteName', v)}
            placeholder="AI 图片生成"
          />
        </Field>

        <Field label="网站域名" hint="用于生成邀请链接、邮件链接等">
          <Input
            type="url"
            value={configForm.siteUrl}
            onChange={(v) => setField('siteUrl', v)}
            placeholder="https://your-domain.com"
          />
        </Field>

        <Field label="注册赠送积分" hint="新用户注册时自动赠送的积分数量">
          <Input
            type="number"
            value={String(configForm.signupCredits)}
            onChange={(v) => setField('signupCredits', parseInt(v) || 0)}
            placeholder="2"
          />
        </Field>
      </div>

      {/* 右栏：首页 Hero 配置 */}
      <div className="space-y-5">
        <SectionTitle icon={Home} title="首页配置" color="bg-blue-500" />

        <Field label="主标题">
          <Input
            value={configForm.heroTitle}
            onChange={(v) => setField('heroTitle', v)}
            placeholder="用文字创造惊艳图片"
          />
        </Field>

        <Field label="副标题">
          <textarea
            value={configForm.heroSubtitle}
            onChange={(e) => setField('heroSubtitle', e.target.value)}
            placeholder="支持文生图..."
            rows={2}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 text-light-text"
          />
        </Field>

        <Field label="特性标签">
          <div className="grid grid-cols-3 gap-2">
            <Input
              value={configForm.heroFeature1}
              onChange={(v) => setField('heroFeature1', v)}
              placeholder="标签 1"
              size="sm"
            />
            <Input
              value={configForm.heroFeature2}
              onChange={(v) => setField('heroFeature2', v)}
              placeholder="标签 2"
              size="sm"
            />
            <Input
              value={configForm.heroFeature3}
              onChange={(v) => setField('heroFeature3', v)}
              placeholder="标签 3"
              size="sm"
            />
          </div>
        </Field>

        <Field label="公告自动折叠延时（秒）" hint="开屏后多少秒自动把公告折叠为汇总条，0 表示不自动折叠">
          <Input
            type="number"
            value={String(configForm.announcementAutoCollapseDelaySec)}
            onChange={(v) => setField('announcementAutoCollapseDelaySec', Math.max(0, parseInt(v) || 0))}
            placeholder="5"
          />
        </Field>

        <Field label="统计代码" hint="粘贴 Umami / Google Analytics 等统计脚本，留空则不加载">
          <textarea
            value={configForm.analyticsTrackingCode}
            onChange={(e) => setField('analyticsTrackingCode', e.target.value)}
            placeholder='<script defer src="https://tracker.zhitu.app/script.js" data-website-id="xxx"></script>'
            rows={3}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 text-light-text font-mono text-sm"
          />
        </Field>
      </div>
    </div>
  );
}

// ---------------- 图片配置 ----------------

function ImageTab({
  configForm,
  setField,
  models,
}: SettingsTabProps & { setField: <K extends keyof ConfigForm>(f: K, v: ConfigForm[K]) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-5">
        <SectionTitle icon={Cpu} title="默认模型" color="bg-teal-500" />

        <Field label="首页默认模型" hint="工作台默认选中的图片生成模型，留空则使用第一个可用模型">
          <select
            value={configForm.defaultModelId}
            onChange={(e) => setField('defaultModelId', e.target.value)}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 text-light-text"
          >
            <option value="">自动（使用第一个可用模型）</option>
            {models.map((m) => (
              <option key={m.id} value={m.modelId}>
                {m.name}（{m.modelId}）{m.isActive ? '' : ' - 已停用'}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="space-y-5">
        <SectionTitle icon={Settings} title="参考图上限" color="bg-cyan-500" />

        <Field label="全站参考图上限兜底" hint="当 API 渠道未单独设置图生图最多参考图时，使用这里的全站兜底值">
          <Input
            type="number"
            value={String(configForm.referenceImageMaxCount)}
            onChange={(v) => setField('referenceImageMaxCount', Math.max(1, parseInt(v) || 1))}
          />
        </Field>
      </div>
    </div>
  );
}

// ---------------- 奖励机制 ----------------

function RewardsTab({
  configForm,
  setField,
}: SettingsTabProps & { setField: <K extends keyof ConfigForm>(f: K, v: ConfigForm[K]) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* 左栏：邀请奖励 */}
      <div className="space-y-5">
        <SectionTitle icon={Gift} title="邀请奖励" color="bg-green-500" />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={configForm.inviteEnabled}
            onChange={(e) => setField('inviteEnabled', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-light-text">启用邀请奖励功能</span>
        </label>

        {configForm.inviteEnabled && (
          <>
            <Field label="邀请人奖励" hint="邀请人成功邀请好友后获得的积分">
              <Input
                type="number"
                value={String(configForm.inviterCredits)}
                onChange={(v) => setField('inviterCredits', parseInt(v) || 0)}
              />
            </Field>
            <Field label="被邀请人奖励" hint="被邀请人注册时额外获得的积分">
              <Input
                type="number"
                value={String(configForm.inviteeCredits)}
                onChange={(v) => setField('inviteeCredits', parseInt(v) || 0)}
              />
            </Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={configForm.invitePhoneOnly}
                onChange={(e) => setField('invitePhoneOnly', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-light-text">仅手机号注册可享受邀请奖励</span>
            </label>
            {configForm.invitePhoneOnly && (
              <p className="text-xs text-light-muted -mt-2">开启后邮箱注册不再发放邀请奖励积分，仅手机号注册有效</p>
            )}
          </>
        )}
      </div>

      {/* 右栏：每日签到 */}
      <div className="space-y-5">
        <SectionTitle icon={CalendarCheck} title="每日签到" color="bg-orange-500" />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={configForm.checkinEnabled}
            onChange={(e) => setField('checkinEnabled', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-light-text">启用每日签到功能</span>
        </label>

        {configForm.checkinEnabled && (
          <>
            <Field label="最小积分奖励" hint="签到时最少获得的积分">
              <Input
                type="number"
                value={String(configForm.checkinMinCredits)}
                onChange={(v) => setField('checkinMinCredits', parseInt(v) || 1)}
              />
            </Field>
            <Field label="最大积分奖励" hint="签到时最多获得的积分（随机范围）">
              <Input
                type="number"
                value={String(configForm.checkinMaxCredits)}
                onChange={(v) => setField('checkinMaxCredits', parseInt(v) || 10)}
              />
            </Field>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- 注册风控 ----------------

function SecurityTab({
  configForm,
  setField,
}: SettingsTabProps & { setField: <K extends keyof ConfigForm>(f: K, v: ConfigForm[K]) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-5">
        <SectionTitle icon={Shield} title="注册渠道开关" color="bg-rose-500" />

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-light-border rounded-xl">
          <input
            type="checkbox"
            checked={configForm.emailRegisterEnabled}
            onChange={(e) => setField('emailRegisterEnabled', e.target.checked)}
            className="w-4 h-4 rounded mt-0.5"
          />
          <div className="flex-1">
            <p className="text-sm text-light-text">允许邮箱注册</p>
            <p className="text-xs text-light-muted mt-0.5">关闭后邮箱注册和发送验证码会被拒绝</p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-light-border rounded-xl">
          <input
            type="checkbox"
            checked={configForm.phoneRegisterEnabled}
            onChange={(e) => setField('phoneRegisterEnabled', e.target.checked)}
            className="w-4 h-4 rounded mt-0.5"
          />
          <div className="flex-1">
            <p className="text-sm text-light-text">允许手机号注册</p>
            <p className="text-xs text-light-muted mt-0.5">关闭后手机注册和发送短信验证码会被拒绝</p>
          </div>
        </label>

        <Field label="允许注册的邮箱后缀" hint="逗号或空格分隔，如 qq.com,163.com">
          <textarea
            value={configForm.allowedEmailDomains}
            onChange={(e) => setField('allowedEmailDomains', e.target.value)}
            placeholder="qq.com,163.com,gmail.com"
            rows={2}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 text-light-text font-mono text-sm"
          />
        </Field>
      </div>

      <div className="space-y-5">
        <SectionTitle icon={Shield} title="频率限制" color="bg-rose-500" />

        <Field label="同 IP 24 小时注册上限" hint="默认 1，建议 1~2">
          <Input
            type="number"
            value={String(configForm.ipRegisterDailyLimit)}
            onChange={(v) => setField('ipRegisterDailyLimit', Math.max(0, parseInt(v) || 0))}
          />
        </Field>

        <Field label="邮箱验证码 IP 小时上限" hint="默认 3">
          <Input
            type="number"
            value={String(configForm.emailCodeIpHourlyLimit)}
            onChange={(v) => setField('emailCodeIpHourlyLimit', Math.max(0, parseInt(v) || 0))}
          />
        </Field>

        <Field label="邮箱验证码 IP 日上限" hint="默认 5">
          <Input
            type="number"
            value={String(configForm.emailCodeIpDailyLimit)}
            onChange={(v) => setField('emailCodeIpDailyLimit', Math.max(0, parseInt(v) || 0))}
          />
        </Field>

        <Field label="邮箱验证码 单邮箱小时上限" hint="默认 3，防止轮换 IP 对单邮箱高频发送">
          <Input
            type="number"
            value={String(configForm.emailCodePerEmailHourlyLimit)}
            onChange={(v) => setField('emailCodePerEmailHourlyLimit', Math.max(0, parseInt(v) || 0))}
          />
        </Field>

        <Field label="邮箱验证码 单邮箱日上限" hint="默认 5">
          <Input
            type="number"
            value={String(configForm.emailCodePerEmailDailyLimit)}
            onChange={(v) => setField('emailCodePerEmailDailyLimit', Math.max(0, parseInt(v) || 0))}
          />
        </Field>
      </div>

      <div className="space-y-5">
        <SectionTitle icon={Shield} title="人机验证" color="bg-rose-500" />

        <label className="flex items-start gap-3 cursor-pointer p-3 border border-light-border rounded-xl">
          <input
            type="checkbox"
            checked={configForm.captchaEnabled}
            onChange={(e) => setField('captchaEnabled', e.target.checked)}
            className="w-4 h-4 rounded mt-0.5"
          />
          <div className="flex-1">
            <p className="text-sm text-light-text">开启图形验证码</p>
            <p className="text-xs text-light-muted mt-0.5">注册和发送验证码时需要输入图形验证码，防止自动化攻击</p>
          </div>
        </label>
      </div>
    </div>
  );
}

// ---------------- 邮件配置 ----------------

function EmailTab({
  configForm,
  setField,
  onTestSMTP,
  onSendTestEmail,
  smtpTesting,
  sendingTestEmail,
  testEmail,
  onTestEmailChange,
}: SettingsTabProps & { setField: <K extends keyof ConfigForm>(f: K, v: ConfigForm[K]) => void }) {
  const isResend = configForm.emailProvider === 'resend';
  const canSendTest = isResend
    ? !!configForm.resendApiKey && !!configForm.resendFromEmail
    : !!configForm.smtpHost;

  return (
    <div className="space-y-6">
      {/* 发送方式切换 */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setField('emailProvider', 'smtp')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            !isResend
              ? 'bg-purple-500 text-white'
              : 'bg-white text-light-text border border-light-border hover:bg-purple-50'
          }`}
        >
          SMTP
        </button>
        <button
          onClick={() => setField('emailProvider', 'resend')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            isResend
              ? 'bg-purple-500 text-white'
              : 'bg-white text-light-text border border-light-border hover:bg-purple-50'
          }`}
        >
          Resend API
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          {isResend ? (
            <>
              <SectionTitle icon={Server} title="Resend API" color="bg-purple-500" />

              <Field label="API Key" hint="在 Resend 后台获取">
                <Input
                  type="password"
                  value={configForm.resendApiKey}
                  onChange={(v) => setField('resendApiKey', v)}
                  placeholder="re_..."
                />
              </Field>

              <Field label="发件人邮箱" hint="需在 Resend 中验证的域名邮箱">
                <Input
                  type="email"
                  value={configForm.resendFromEmail}
                  onChange={(v) => setField('resendFromEmail', v)}
                  placeholder="noreply@zhitu.app"
                />
              </Field>

              <Field label="发件人名称">
                <Input
                  value={configForm.resendFromName}
                  onChange={(v) => setField('resendFromName', v)}
                  placeholder="AI 图片生成"
                />
              </Field>
            </>
          ) : (
            <>
              <SectionTitle icon={Server} title="SMTP 服务器" color="bg-purple-500" />

              <Field label="SMTP 服务器">
                <Input
                  value={configForm.smtpHost}
                  onChange={(v) => setField('smtpHost', v)}
                  placeholder="smtp.example.com"
                />
              </Field>

              <Field label="端口">
                <Input
                  type="number"
                  value={String(configForm.smtpPort)}
                  onChange={(v) => setField('smtpPort', parseInt(v) || 465)}
                  placeholder="465"
                />
              </Field>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={configForm.smtpSecure}
                  onChange={(e) => setField('smtpSecure', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-light-text">使用 SSL/TLS 加密连接</span>
              </label>

              <Field label="邮箱账号">
                <Input
                  value={configForm.smtpUser}
                  onChange={(v) => setField('smtpUser', v)}
                  placeholder="noreply@example.com"
                />
              </Field>

              <Field label="邮箱密码 / 授权码">
                <Input
                  type="password"
                  value={configForm.smtpPass}
                  onChange={(v) => setField('smtpPass', v)}
                  placeholder="******"
                />
              </Field>

              <div className="pt-1">
                <button
                  onClick={onTestSMTP}
                  disabled={smtpTesting || !configForm.smtpHost || !configForm.smtpUser}
                  className="px-3 py-2 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 disabled:opacity-50 transition-colors flex items-center gap-2 text-sm"
                >
                  {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  测试连接
                </button>
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-light-border">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => onTestEmailChange(e.target.value)}
              placeholder="测试邮箱"
              className="flex-1 min-w-[160px] bg-white border border-light-border rounded-xl px-3 py-2 text-sm"
            />
            <button
              onClick={onSendTestEmail}
              disabled={sendingTestEmail || !testEmail || !canSendTest}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 disabled:opacity-50 transition-colors flex items-center gap-2 text-sm"
            >
              {sendingTestEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              发送测试
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <SectionTitle icon={Mail} title="发件人 + 模板" color="bg-purple-500" />

          {!isResend && (
            <>
              <Field label="发件人邮箱" hint="留空则使用邮箱账号">
                <Input
                  type="email"
                  value={configForm.smtpFrom}
                  onChange={(v) => setField('smtpFrom', v)}
                  placeholder="noreply@example.com"
                />
              </Field>

              <Field label="发件人名称">
                <Input
                  value={configForm.smtpFromName}
                  onChange={(v) => setField('smtpFromName', v)}
                  placeholder="AI 图片生成"
                />
              </Field>
            </>
          )}

          <Field
            label="验证码邮件模板（HTML）"
            hint={`支持变量：{{siteName}} {{userName}} {{code}} {{expiryMinutes}} {{year}}`}
          >
            <textarea
              value={configForm.verifyCodeTemplate}
              onChange={(e) => setField('verifyCodeTemplate', e.target.value)}
              placeholder="留空使用默认模板"
              rows={10}
              className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 text-light-text font-mono text-xs"
            />
          </Field>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-0.5">提示</p>
              <p>API 配置已迁移至「API 渠道管理」，请在该页面配置 API Key。</p>
            </div>
          </div>

          <div className="pt-3 border-t border-light-border space-y-5">
            <SectionTitle icon={AlertCircle} title="错误告警" color="bg-rose-500" />

            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-light-border p-3">
              <input
                type="checkbox"
                checked={configForm.errorAlertEnabled}
                onChange={(e) => setField('errorAlertEnabled', e.target.checked)}
                className="mt-1 h-4 w-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-light-text">启用错误报警</p>
                <p className="mt-0.5 text-xs text-light-muted">
                  当固定统计窗口内的失败日志条数超过阈值时，使用当前邮件配置发送 1 封告警邮件。
                </p>
              </div>
            </label>

            <Field label="告警收件邮箱" hint="第一版仅支持单个邮箱">
              <Input
                type="email"
                value={configForm.errorAlertRecipient}
                onChange={(v) => setField('errorAlertRecipient', v)}
                placeholder="alert@example.com"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="统计窗口（分钟）" hint="按固定时间窗口统计，例如 5 分钟">
                <Input
                  type="number"
                  value={String(configForm.errorAlertWindowMinutes)}
                  onChange={(v) => setField('errorAlertWindowMinutes', Math.max(1, parseInt(v) || 1))}
                />
              </Field>

              <Field label="触发阈值（条）" hint="窗口内失败日志条数大于该值时告警">
                <Input
                  type="number"
                  value={String(configForm.errorAlertThreshold)}
                  onChange={(v) => setField('errorAlertThreshold', Math.max(1, parseInt(v) || 1))}
                />
              </Field>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- 公共子组件 ----------------

function SectionTitle({
  icon: Icon,
  title,
  color,
}: {
  icon: typeof Settings;
  title: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h4 className="text-base font-semibold text-light-text">{title}</h4>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-light-text mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-xs text-light-muted mt-1">{hint}</p>}
    </div>
  );
}

function Input({
  type = 'text',
  value,
  onChange,
  placeholder,
  size = 'md',
}: {
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
}) {
  const padding = size === 'sm' ? 'px-3 py-2 text-sm' : 'px-4 py-2.5';
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-white border border-light-border rounded-xl ${padding} text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20`}
    />
  );
}
